/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel } from '../../../../core/models';
import { NotifyService, UtilsService } from '../../../../core/services';
import { Router } from '@angular/router';
import { TableMultiSelectModel } from '../../../../core/models/tableMultiSelect.model';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-notifications',
    styleUrls: ['./notifications.component.scss'],
    templateUrl: './notifications.component.html',
})

export class NotificationsComponent extends BaseComponent {
    /**
     * Rows displayed in the table.
     */
    public tableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching.
     */
    public tempRows: any[] = [];

    /**
     * To store the actions and text for the tables select all functions.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * Store our dropdown items.
     */
    public dropdownContent: any[] = [
        <DropdownModel>{ key: 9, value: 'All' },
        <DropdownModel>{ key: 1, value: 'Error' },
        <DropdownModel>{ key: 2, value: 'Warning' },
        <DropdownModel>{ key: 3, value: 'Success' },
        <DropdownModel>{ key: 4, value: 'Info' }
    ];

    constructor(
        private notifyService: NotifyService,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        // Get our initial list of system notifications
        this.populateTableRows(this.notifyService.getCopyOfNotifications());

        // Subscribe to system notifications changes
        this.notifyService.updateNotificationsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                notifications => {
                    this.populateTableRows(notifications);
                }
            );

        this.setSelectAllActionDotButtons();
    }

    /**
     * Dismiss all System/Top notifications
     */
    public clearNotifications() {
        this.notifyService.clearAll();
    }

    /**
     * Clear the selected notification.
     * @param id
     */
    public clearSelectedNotification(id): void {
        this.notifyService.removeNotificationById(id);
    }

    /**
     * Update the table with data from the selected backend.
     * @param event
     */
    public updateTable(event) {
        if (event === 9) {
            this.tableRows = [...this.tempRows];
        } else {
            this.tableRows = [...this.tempRows.filter(item => item.level === event)];
        }
    }

    /**
     * Navigation to url, prepend appliance ID.
     * @param {string} url
     */
    public navigate(url: string): void {
        if (!url) {
            return;
        }

        const lbUser = JSON.parse(localStorage.getItem('lbUser'));
        const applianceId = lbUser && lbUser.applianceId;
        this.router.navigateByUrl(`/lb/${applianceId}/${url}`).then().catch();
    }

    /**
     * Populate table rows.
     * @param notifications
     * @private
     */
    private populateTableRows(notifications) {
        const sortedNotifications = this.utilsService.sortArrayByProperty(notifications, 'level');

        this.tableRows = [];

        for (const notification of sortedNotifications) {
            const date = this.utilsService.createDateTimeString(notification.date);

            this.tableRows.push({
                id: notification.id,
                level: notification.level,
                url: notification.url,
                message: notification.message,
                date
            });
        }

        this.tempRows = [...this.tableRows];
    }

    /**
     * Set the select all action dots object to store the button text and action.
     * @private
     */
    private setSelectAllActionDotButtons(): void {
        this.multiSelectActions = [
            {
                buttonText: 'notifications_delete_selected',
                buttonAction: this.clearNotifications.bind(this),
                buttonDisabled: false
            }
        ];
    }
}
